import React, { useEffect, useState, useMemo, useCallback, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiStar } from "react-icons/fi";
import { 
  FaSearch, 
  FaFilter, 
  FaTimes, 
  FaShoppingCart,
  FaEye,
  FaHeart,
  FaRegHeart,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaMinus,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaStar,
  FaWhatsapp
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import api, { favoritesAPI } from '../api/axios';
import Footer from '../components/Footer';
import WppChat from "../components/whatsappchat";

// Constantes para colores
const COLORe = {
  primary: '#0C4B45',
  primaryLight: '#83F4E9',
  primaryDark: '#083D38',
  secondary: '#662D8F',
  secondaryLight: '#F2A9FD',
  textDark: '#0C4B45',
  textLight: '#E0F3EB',
  background: '#F0F9F5'
};

// Contexto de carrito
const CartContext = createContext();

// Proveedor de carrito
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = useCallback((product, quantity) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product._id === product._id);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.product._id === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevCart, { product, quantity }];
      }
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setCart(prevCart => {
      if (quantity <= 0) {
        return prevCart.filter(item => item.product._id !== productId);
      }
      
      return prevCart.map(item =>
        item.product._id === productId
          ? { ...item, quantity }
          : item
      );
    });
  }, []);

  const value = {
    cart,
    addToCart,
    updateQuantity
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personalizado para usar el carrito
export const useCartContext = () => useContext(CartContext);

// Utilidad para obtener URLs de imágenes
const getImageUrl = (imageName) => {
  if (!imageName) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240"%3E%3Crect width="100%25" height="100%25" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236b7280" font-family="sans-serif" font-size="16"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
  
  if (imageName.startsWith('http')) {
    return imageName;
  }
  
  if (imageName.includes('uploads/')) {
    return imageName.startsWith('/') ? imageName : `/${imageName}`;
  }
  
  return `/uploads/products/${imageName}`;
};

const imageFallback = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240"%3E%3Crect width="100%25" height="100%25" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236b7280" font-family="sans-serif" font-size="16"%3EImagen no disponible%3C/text%3E%3C/svg%3E';

// Utilidad para calcular descuento
const calculateDiscount = (originalPrice, price) => {
  if (!originalPrice || !price) return 0;
  return Math.round(((originalPrice - price) / originalPrice) * 100);
};

// Componente de Loading
const LoadingState = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#0C4B45]/5 to-[#83F4E9]/5">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-16 h-16 border-4 border-[#662D8F] border-t-transparent rounded-full"
    />
    <motion.p 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-6 text-lg text-[#0C4B45] font-medium"
    >
      Cargando nuestra colección premium...
    </motion.p>
  </div>
);

// Componente de Error
const ErrorState = ({ error, onRetry }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#0C4B45]/5 to-[#83F4E9]/5 p-6">
    <motion.div 
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center"
    >
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </div>
      <h2 className="text-xl font-bold text-[#0C4B45] mb-2">Error al cargar productos</h2>
      <p className="text-gray-600 mb-6">{error}</p>
      <motion.button
        onClick={onRetry}
        className="w-full py-3 px-6 bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white rounded-xl font-medium hover:from-[#512577] hover:to-[#e895fc] transition-all"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Reintentar
      </motion.button>
    </motion.div>
  </div>
);

// Componente de ProductCard
const ProductCard = ({ 
  product, 
  isFavorite, 
  onToggleFavorite, 
  onQuickView, 
  cart, 
  onAddToCart, 
  onUpdateQuantity,
  favoriteLoading 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [localSelectedImage, setLocalSelectedImage] = useState(0);
  
  const cartItem = cart.find(item => item.product._id === product._id);
  const [quantity, setQuantity] = useState(cartItem ? cartItem.quantity : 1);
  const isAdded = !!cartItem;

  const images = useMemo(() => [
    getImageUrl(product.image),
    ...((product.images || product.additionalImages || []).filter(img => img !== product.image).map(img => getImageUrl(img)))
  ], [product.image, product.images, product.additionalImages]);

  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(1);
    }
  }, [cartItem]);

  const handleAddToCart = useCallback((e) => {
    e.preventDefault();
    onAddToCart(product, quantity);
  }, [product, quantity, onAddToCart]);

  const handleWhatsAppProduct = useCallback((e) => {
    e.preventDefault();
    const message = `Hola, estoy interesado en el producto: ${product.name}. ¿Me pueden brindar más información?`;
    window.open(`https://wa.me/573005934724?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  }, [product.name]);

  const handleUpdateQuantity = useCallback((e, newQuantity) => {
    e.preventDefault();
    if (newQuantity < 1 || newQuantity > (product.stock || 10)) return;
    
    setQuantity(newQuantity);
    if (isAdded) onUpdateQuantity(product._id, newQuantity);
  }, [product.stock, product._id, isAdded, onUpdateQuantity]);

  const discount = calculateDiscount(product.originalPrice, product.price);

  return (
    <motion.div 
      className="relative group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 mt-16 bg-gradient-to-br from-[#0C4B45]/10 to-[#83F4E9]/10 rounded-3xl shadow-lg transform group-hover:-translate-y-2 transition-transform duration-300"></div>
      
      <div className="relative bg-white rounded-2xl overflow-hidden shadow-md h-full flex flex-col">
        {/* Badges */}
        <div className="absolute top-4 left-4 z-10 flex space-x-2">
          {product.isNew && (
            <motion.span 
              className="bg-[#F2A9FD] text-[#0C4B45] text-xs font-bold px-3 py-1 rounded-full shadow-sm"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              NUEVO
            </motion.span>
          )}
          {discount > 0 && (
            <motion.span 
              className="bg-[#662D8F] text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              -{discount}%
            </motion.span>
          )}
        </div>

        {/* Favorite Button */}
        <motion.button 
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite(product._id);
          }}
          disabled={favoriteLoading}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 shadow-md hover:bg-white transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {favoriteLoading ? (
            <div className="w-4 h-4 border-t-2 border-[#F2A9FD] rounded-full animate-spin"></div>
          ) : isFavorite ? (
            <FaHeart className="text-[#F2A9FD] text-lg" />
          ) : (
            <FaRegHeart className="text-gray-600 hover:text-[#F2A9FD] text-lg" />
          )}
        </motion.button>

        {/* Imagen del producto */}
        <Link to={`/product/${product._id}`} className="block relative flex-grow">
          <div className="relative h-64 w-full bg-gradient-to-br from-[#0C4B45]/5 to-[#83F4E9]/5">
            <img 
              src={images[localSelectedImage]} 
              alt={product.name}
              className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = imageFallback; }}
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setLocalSelectedImage((prev) => (prev - 1 + images.length) % images.length);
                  }}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-md hover:bg-white text-[#662D8F]"
                >
                  <FaChevronLeft />
                </button>
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setLocalSelectedImage((prev) => (prev + 1) % images.length);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-md hover:bg-white text-[#662D8F]"
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            <AnimatePresence>
              {isHovered && (
                <motion.div 
                  className="absolute inset-0 bg-black/20 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.button 
                    onClick={(e) => {
                      e.preventDefault();
                      onQuickView(product);
                    }}
                    className="bg-white text-[#662D8F] font-medium px-4 py-2 rounded-lg flex items-center hover:bg-[#662D8F] hover:text-white transition-colors shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaEye className="mr-2" /> Vista Rápida
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Link>
        
        {/* Información del producto */}
        <div className="p-5 pt-3 flex flex-col">
          <Link to={`/product/${product._id}`} className="mb-2">
            <h3 className="font-bold text-lg text-[#0C4B45] hover:text-[#662D8F] transition-colors line-clamp-1">
              {product.name}
            </h3>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
              {product.shortDescription || product.description || 'Descripción no disponible'}
            </p>
          </Link>

          <div className="flex items-center gap-1 mb-3" aria-label={`Calificación ${product.rating || 0} de 5`}>
            {[1, 2, 3, 4, 5].map(star => <FaStar key={star} className={star <= Math.round(product.rating || 0) ? 'text-yellow-400' : 'text-gray-300'} />)}
            <span className="ml-1 text-xs text-gray-500">{product.rating || 0} ({product.reviewCount || 0})</span>
          </div>

          {/* Precio y stock */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="font-bold text-xl text-[#662D8F]">
                ${product.price?.toLocaleString() || '0.00'}
              </span>
              {product.originalPrice && product.price < product.originalPrice && (
                <span className="text-sm text-gray-400 line-through ml-2">
                  ${product.originalPrice.toLocaleString()}
                </span>
              )}
            </div>
            {product.stock !== undefined && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                product.stock > 5 ? 'bg-[#83F4E9]/20 text-[#0C4B45]' : 'bg-[#F2A9FD]/20 text-[#662D8F]'
              }`}>
                {product.stock > 5 ? 'Disponible' : 'Últimas unidades'}
              </span>
            )}
          </div>

          {/* Información de Garantía - CORREGIDO Y MEJORADO */}
          {product.warranty && (
            <div className="flex items-center text-sm text-gray-600 mb-4 p-2 bg-gray-50 rounded-lg">
              <FiStar className="mr-2 text-yellow-500 flex-shrink-0" />
              <span className="font-medium">Garantía: </span>
              <span className="ml-1 text-[#0C4B45] font-semibold">
                {product.warranty} {product.warranty === '1' ? 'mes' : 'meses'}
              </span>
            </div>
          )}

          {/* Controles de cantidad y carrito */}
          <div className="flex items-center gap-3 mt-auto">
            <motion.button
              onClick={handleAddToCart}
              className={`flex-1 py-3 rounded-lg flex items-center justify-center transition-all duration-300 overflow-hidden relative ${
                isAdded 
                  ? 'bg-[#83F4E9] text-[#0C4B45]' 
                  : 'bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white'
              }`}
              whileHover={!isAdded ? { 
                scale: 1.02,
                boxShadow: '0 5px 15px rgba(102, 45, 143, 0.3)'
              } : {}}
              whileTap={!isAdded ? { scale: 0.98 } : {}}
            >
              <span className="relative z-10 flex items-center justify-center">
                {isAdded ? (
                  <>
                    <FaCheck className="mr-2" /> Añadido
                  </>
                ) : (
                  <>
                    <FaShoppingCart className="mr-2 text-lg" /> Comprar
                  </>
                )}
              </span>
            </motion.button>

            <motion.button
              type="button"
              onClick={handleWhatsAppProduct}
              aria-label={`Consultar ${product.name} por WhatsApp`}
              title="Consultar por WhatsApp"
              className="shrink-0 rounded-lg bg-[#00887a] p-3 text-white shadow-sm transition-colors hover:bg-[#0b4741]"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaWhatsapp className="text-xl" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Componente de Paginación
const Pagination = ({ currentPage, totalPages, totalProducts, productsPerPage, onPageChange }) => {
  const maxVisiblePages = 5;

  const getPaginationRange = useCallback(() => {
    const range = [];
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }, [currentPage, totalPages, maxVisiblePages]);

  const paginationRange = getPaginationRange();

  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [totalPages, onPageChange]);

  if (totalPages <= 1) return null;

  return (
    <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="text-sm text-gray-600">
        Mostrando {(currentPage - 1) * productsPerPage + 1} - {Math.min(currentPage * productsPerPage, totalProducts)} de {totalProducts} productos
      </div>
      
      <div className="flex items-center gap-1">
        <motion.button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-[#662D8F] hover:bg-[#F2A9FD]/20'}`}
          whileHover={{ scale: currentPage === 1 ? 1 : 1.1 }}
          whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
        >
          <FaAngleDoubleLeft />
        </motion.button>

        <motion.button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-[#662D8F] hover:bg-[#F2A9FD]/20'}`}
          whileHover={{ scale: currentPage === 1 ? 1 : 1.1 }}
          whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
        >
          <FaAngleLeft />
        </motion.button>

        {paginationRange.map((page) => (
          <motion.button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              currentPage === page
                ? 'bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white shadow-md'
                : 'text-[#0C4B45] hover:bg-[#83F4E9]/20'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {page}
          </motion.button>
        ))}

        <motion.button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-[#662D8F] hover:bg-[#F2A9FD]/20'}`}
          whileHover={{ scale: currentPage === totalPages ? 1 : 1.1 }}
          whileTap={{ scale: currentPage === totalPages ? 1 : 0.95 }}
        >
          <FaAngleRight />
        </motion.button>

        <motion.button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-[#662D8F] hover:bg-[#F2A9FD]/20'}`}
          whileHover={{ scale: currentPage === totalPages ? 1 : 1.1 }}
          whileTap={{ scale: currentPage === totalPages ? 1 : 0.95 }}
        >
          <FaAngleDoubleRight />
        </motion.button>
      </div>
    </div>
  );
};

// Componente de Filtros
const FiltersSidebar = ({ categories, categoryFilter, setCategoryFilter, sortOption, setSortOption, mobileFiltersOpen, setMobileFiltersOpen }) => (
  <>
    <button
      type="button"
      className="lg:hidden flex items-center justify-center w-full py-2 px-4 border border-gray-300 rounded-xl bg-white shadow-sm text-sm font-medium text-[#0C4B45] hover:bg-gray-50 mb-4"
      onClick={() => setMobileFiltersOpen(true)}
    >
      <FaFilter className="mr-2" />
      Filtros
    </button>

    <div className="hidden lg:block">
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-medium text-[#0C4B45] mb-4">Filtrar por</h3>
        
        <div className="mb-6">
          <h4 className="text-sm font-medium text-[#0C4B45] mb-3">Categorías</h4>
          <div className="space-y-2">
            {categories.map(category => (
              <div key={category} className="flex items-center">
                <input
                  id={`category-${category}`}
                  name="category"
                  type="radio"
                  className="h-4 w-4 border-gray-300 rounded text-[#662D8F] focus:ring-[#662D8F]"
                  checked={categoryFilter === category}
                  onChange={() => setCategoryFilter(category)}
                />
                <label htmlFor={`category-${category}`} className="ml-3 text-sm text-gray-700 capitalize">
                  {category === 'all' ? 'Todas las categorías' : category}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-[#0C4B45] mb-3">Ordenar por</h4>
          <select
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent rounded-xl"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="default">Predeterminado</option>
            <option value="price-asc">Precio: Menor a mayor</option>
            <option value="price-desc">Precio: Mayor a menor</option>
            <option value="name-asc">Nombre: A-Z</option>
            <option value="name-desc">Nombre: Z-A</option>
          </select>
        </div>
      </div>
    </div>

    <AnimatePresence>
      {mobileFiltersOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 overflow-y-auto lg:hidden"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={() => setMobileFiltersOpen(false)}></div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="inline-block align-bottom bg-white rounded-t-2xl shadow-xl transform transition-all sm:align-middle sm:max-w-lg sm:w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-[#0C4B45]">Filtros</h3>
                  <button
                    type="button"
                    className="-mr-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400 hover:text-gray-500"
                    onClick={() => setMobileFiltersOpen(false)}
                    aria-label="Cerrar filtros"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-[#0C4B45] mb-3">Categorías</h4>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <div key={category} className="flex items-center">
                        <input
                          id={`mobile-category-${category}`}
                          name="mobile-category"
                          type="radio"
                          className="h-4 w-4 border-gray-300 rounded text-[#662D8F] focus:ring-[#662D8F]"
                          checked={categoryFilter === category}
                          onChange={() => setCategoryFilter(category)}
                        />
                        <label htmlFor={`mobile-category-${category}`} className="ml-3 text-sm text-gray-700 capitalize">
                          {category === 'all' ? 'Todas las categorías' : category}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-[#0C4B45] mb-3">Ordenar por</h4>
                  <select
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent rounded-xl"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <option value="default">Predeterminado</option>
                    <option value="price-asc">Precio: Menor a mayor</option>
                    <option value="price-desc">Precio: Mayor a menor</option>
                    <option value="name-asc">Nombre: A-Z</option>
                    <option value="name-desc">Nombre: Z-A</option>
                  </select>
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    className="w-full py-3 px-6 bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white rounded-xl font-medium hover:from-[#512577] hover:to-[#e895fc] transition-all"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    Aplicar filtros
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
);

// Componente de Vista Rápida - ACTUALIZADO PARA MOSTRAR GARANTÍA
const QuickViewModal = ({ product, onClose, isFavorite, onToggleFavorite, cart, onAddToCart, onUpdateQuantity, favoriteLoading }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const cartItem = cart.find(item => item.product._id === product._id);
  const quantity = cartItem ? cartItem.quantity : 1;

  const images = useMemo(() => [
    getImageUrl(product.image),
    ...((product.images || product.additionalImages || []).filter(img => img !== product.image).map(img => getImageUrl(img)))
  ], [product.image, product.images, product.additionalImages]);

  const discount = calculateDiscount(product.originalPrice, product.price);

  return (
    <motion.div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-3xl font-bold text-[#0C4B45]">{product.name}</h3>
            <motion.button 
              onClick={onClose}
              className="text-gray-500 hover:text-[#662D8F] p-2 rounded-full hover:bg-gray-100"
              whileHover={{ rotate: 90 }}
              aria-label="Cerrar vista rápida"
            >
              ✕
            </motion.button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#0C4B45]/10 to-[#83F4E9]/10 p-8 rounded-xl flex items-center justify-center h-80">
                <img 
                  src={images[selectedImageIndex]} 
                  alt={product.name} 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {images.map((img, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      whileHover={{ y: -3 }}
                      className={`border-2 rounded-xl overflow-hidden transition-all ${
                        selectedImageIndex === index 
                          ? 'border-[#662D8F] shadow-md' 
                          : 'border-transparent hover:border-gray-200'
                      }`}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img 
                        src={img} 
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-20 object-cover bg-gray-50"
                      />
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <p className="text-gray-700 mb-8 leading-relaxed">
                {product.description || 'Descripción no disponible'}
              </p>
              
              <div className="mb-8">
                <span className="font-bold text-3xl text-[#662D8F]">
                  ${product.price?.toLocaleString() || '0.00'}
                </span>
                {product.originalPrice && product.price < product.originalPrice && (
                  <span className="text-lg text-gray-400 line-through ml-3">
                    ${product.originalPrice.toLocaleString()}
                  </span>
                )}
                {discount > 0 && (
                  <span className="ml-3 bg-[#F2A9FD]/20 text-[#662D8F] text-sm font-bold px-3 py-1 rounded-full">
                    {discount}% OFF
                  </span>
                )}
              </div>
              
              {/* Información de Garantía en Vista Rápida */}
              {product.warranty && (
                <div className="flex items-center text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded-lg">
                  <FiStar className="mr-3 text-yellow-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Garantía del producto: </span>
                    <span className="text-[#0C4B45] font-semibold">
                      {product.warranty} {product.warranty === '1' ? 'mes' : 'meses'}
                    </span>
                  </div>
                </div>
              )}
              
              {product.stock !== undefined && (
                <div className="mb-8">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Disponibilidad:</span>
                    <span className={`font-medium ${
                      product.stock > 5 ? 'text-[#0C4B45]' : 'text-[#662D8F]'
                    }`}>
                      {product.stock} unidades disponibles
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className={`h-full rounded-full ${
                        product.stock > 5 ? 'bg-[#83F4E9]' : 'bg-[#F2A9FD]'
                      }`} 
                      style={{ 
                        width: `${Math.min(100, (product.stock / 10) * 100)}%`,
                        transition: 'width 0.5s ease'
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-6 mb-8">
                <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (quantity > 1) {
                        if (cartItem) {
                          onUpdateQuantity(product._id, quantity - 1);
                        }
                      }
                    }}
                    className="px-4 py-3 hover:bg-gray-100 text-[#662D8F] transition-colors"
                    disabled={quantity <= 1 || !cartItem}
                    aria-label="Reducir cantidad"
                  >
                    <FaMinus />
                  </button>
                  <span className="px-5 py-2 font-medium text-lg w-12 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (quantity < (product.stock || 10)) {
                        if (cartItem) {
                          onUpdateQuantity(product._id, quantity + 1);
                        } else {
                          onAddToCart(product, quantity + 1);
                        }
                      }
                    }}
                    className="px-4 py-3 hover:bg-gray-100 text-[#662D8F] transition-colors"
                    disabled={quantity >= (product.stock || 10)}
                    aria-label="Aumentar cantidad"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <motion.button
                  onClick={(e) => {
                    e.preventDefault();
                    if (cartItem) {
                      toast.success(
                        <div className="flex items-center">
                          <FaShoppingCart className="text-[#0C4B45] mr-2" />
                          <span>Producto ya en el carrito</span>
                        </div>,
                        {
                          position: "top-right",
                          className: 'bg-white border-l-4 border-[#662D8F] shadow-lg'
                        }
                      );
                    } else {
                      onAddToCart(product, quantity);
                      toast.success(
                        <div className="flex items-center">
                          <FaShoppingCart className="text-[#0C4B45] mr-2" />
                          <span>{product.name} agregado al carrito</span>
                        </div>,
                        {
                          position: "top-right",
                          className: 'bg-white border-l-4 border-[#662D8F] shadow-lg'
                        }
                      );
                    }
                    onClose();
                  }}
                  className={`flex-1 py-4 rounded-xl flex items-center justify-center transition-all duration-300 relative overflow-hidden ${
                    cartItem 
                      ? 'bg-[#83F4E9] text-[#0C4B45]' 
                      : 'bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white'
                  }`}
                  whileHover={!cartItem ? { 
                    scale: 1.02,
                    boxShadow: '0 5px 20px rgba(102, 45, 143, 0.4)'
                  } : {}}
                  whileTap={!cartItem ? { scale: 0.98 } : {}}
                >
                  <span className="relative z-10 flex items-center text-lg font-medium">
                    {cartItem ? (
                      <>
                        <FaCheck className="mr-3" /> Añadido al carrito
                      </>
                    ) : (
                      <>
                        <FaShoppingCart className="mr-3 text-xl" /> Agregar al carrito
                      </>
                    )}
                  </span>
                </motion.button>
                
                <motion.button
                  onClick={(e) => {
                    e.preventDefault();
                    onToggleFavorite(product._id);
                  }}
                  disabled={favoriteLoading}
                  className={`p-4 rounded-xl flex items-center justify-center text-xl ${
                    isFavorite 
                      ? 'bg-[#F2A9FD]/20 text-[#662D8F]' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
                >
                  {favoriteLoading ? (
                    <div className="w-5 h-5 border-t-2 border-[#F2A9FD] rounded-full animate-spin"></div>
                  ) : isFavorite ? (
                    <FaHeart />
                  ) : (
                    <FaRegHeart />
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Componente principal
const ProductsListPage = () => {
  // Estados principales
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOption, setSortOption] = useState('default');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState({});

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);

  const { cart, addToCart, updateQuantity } = useCart();

  // Un filtro nuevo siempre comienza desde la primera página disponible.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, sortOption]);

  // Obtener productos con paginación
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await api.get('/products', { params: { page: currentPage, limit: productsPerPage } });
        setProducts(res.data.data || res.data.products || []);
        setTotalProducts(res.data.total || res.data.totalProducts || 0);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Error al cargar los productos');
        toast.error('Error al cargar los productos', {
          position: "top-right",
          className: 'bg-white border-l-4 border-red-500 shadow-lg'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, productsPerPage]);

  // Cargar favoritos al iniciar
  useEffect(() => {
    const loadFavorites = async () => {
      if (!localStorage.getItem('token')) return;
      try {
        const response = await favoritesAPI.getFavorites();
        const favoriteIds = response.data.map(fav => fav._id);
        setFavorites(favoriteIds);
      } catch (error) {
        if (error.response?.status !== 401) console.error('Error loading favorites:', error);
      }
    };

    loadFavorites();
  }, []);

  // Filtrar y ordenar productos
  const processedProducts = useMemo(() => {
    const filtered = products.filter(product => {
      const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'price-asc': return (a.price || 0) - (b.price || 0);
        case 'price-desc': return (b.price || 0) - (a.price || 0);
        case 'name-asc': return (a.name || '').localeCompare(b.name || '');
        case 'name-desc': return (b.name || '').localeCompare(a.name || '');
        default: return 0;
      }
    });
  }, [products, searchTerm, categoryFilter, sortOption]);

  // Obtener categorías únicas
  const categories = useMemo(() => 
    ['all', ...new Set(products.map(product => product.category).filter(Boolean))], 
    [products]
  );

  // Toggle favoritos con API
  const toggleFavorite = useCallback(async (productId) => {
    if (!localStorage.getItem('token')) {
      toast.info('Inicia sesión para guardar productos en favoritos.');
      return;
    }
    setFavoriteLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await favoritesAPI.toggleFavorite(productId);
      setFavorites(prev => 
        prev.includes(productId) 
          ? prev.filter(id => id !== productId) 
          : [...prev, productId]
      );
      
      // Mostrar notificación
      const product = products.find(p => p._id === productId);
      if (product) {
        toast.success(
          response.data.isFavorite 
            ? `${product.name} agregado a favoritos`
            : `${product.name} removido de favoritos`,
          {
            position: "top-right",
            className: 'bg-white border-l-4 border-[#662D8F] shadow-lg'
          }
        );
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error al actualizar favoritos', {
        position: "top-right",
        className: 'bg-white border-l-4 border-red-500 shadow-lg'
      });
    } finally {
      setFavoriteLoading(prev => ({ ...prev, [productId]: false }));
    }
  }, [products]);

  // Handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleQuickView = useCallback((product) => {
    setQuickViewProduct(product);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewProduct(null);
  }, []);

  const handleAddToCart = useCallback((product, quantity) => {
    addToCart(product, quantity);
    
    toast.success(
      <div className="flex items-center">
        <FaShoppingCart className="text-[#0C4B45] mr-2" />
        <span>{product.name} agregado al carrito</span>
      </div>,
      {
        position: "top-right",
        className: 'bg-white border-l-4 border-[#662D8F] shadow-lg'
      }
    );
  }, [addToCart]);

  const handleUpdateQuantity = useCallback((productId, quantity) => {
    updateQuantity(productId, quantity);
  }, [updateQuantity]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  // Estados de carga
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={handleRetry} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0C4B45]/5 to-[#83F4E9]/5">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mt-24 mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-3xl font-bold text-[#0C4B45]">Colección Premium</h1>
              <p className="text-[#0C4B45]/80 mt-1">Productos de alta calidad seleccionados para ti</p>
            </div>
            
            {/* Barra de búsqueda desktop */}
            <div className="relative w-full md:w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-[#0C4B45]/60" />
              </div>
              <input
                type="text"
                placeholder="Buscar productos..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filtros sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <FiltersSidebar
              categories={categories}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              sortOption={sortOption}
              setSortOption={setSortOption}
              mobileFiltersOpen={mobileFiltersOpen}
              setMobileFiltersOpen={setMobileFiltersOpen}
            />
          </aside>

          {/* Product grid */}
          <div className="flex-1">
            {processedProducts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-[#0C4B45]">No se encontraron productos</h3>
                <p className="mt-2 text-gray-600 mb-6">Intenta ajustar tus filtros de búsqueda</p>
                <motion.button
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('all');
                    setSortOption('default');
                  }}
                  className="py-2 px-6 border border-transparent rounded-xl text-sm font-medium text-white bg-[#0C4B45] hover:bg-[#083D38] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0C4B45]"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Limpiar filtros
                </motion.button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {processedProducts.map(product => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      isFavorite={favorites.includes(product._id)}
                      onToggleFavorite={toggleFavorite}
                      onQuickView={handleQuickView}
                      cart={cart}
                      onAddToCart={handleAddToCart}
                      onUpdateQuantity={handleUpdateQuantity}
                      favoriteLoading={favoriteLoading[product._id] || false}
                    />
                  ))}
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalProducts / productsPerPage)}
                  totalProducts={totalProducts}
                  productsPerPage={productsPerPage}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        </div>
        
      </main>

      <AnimatePresence>
        {quickViewProduct && (
          <QuickViewModal
            product={quickViewProduct}
            onClose={handleCloseQuickView}
            isFavorite={favorites.includes(quickViewProduct._id)}
            onToggleFavorite={toggleFavorite}
            cart={cart}
            onAddToCart={handleAddToCart}
            onUpdateQuantity={handleUpdateQuantity}
            favoriteLoading={favoriteLoading[quickViewProduct._id] || false}
          />
        )}
      </AnimatePresence>
      
      <WppChat />
      <Footer/>
    </div>
  );
};

export default ProductsListPage;
